import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ActionButton, ComboBox, ComboBoxItem, Text } from "@react-spectrum/s2";
import { AlertTriangle as Alert, Close } from '../icons/s2';
import { LoadingState } from '@react-types/shared';
import { CCDomainUtilityOption } from '../../types';
import { fetchDomainUtilityConfigurations } from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { formatDate } from '../../utils/dateUtils';
import { appendUniqueBy } from '../../utils/appendUnique';
import { useProviderMetadata } from '../../hooks/useProviderMetadata';
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
export function TypeBadge({ variant, label }: Readonly<{
    variant: 'ootb' | 'custom' | 'deprecated' | 'default';
    label: string;
}>) {
    return (<div className={`cc-type-badge cc-type-badge--${variant}`}>
      <div className="cc-type-badge-dot"/>
      <span>{label}</span>
    </div>);
}
export interface ConfigurationStatus {
    source: 'system_ootb' | 'org_custom';
    is_deprecated: boolean;
    is_default: boolean;
}
/** Source/deprecated/default badges for the Command Center current-configuration
 * view (DomainDetailPanel.tsx) -- not used by the picker below, which only
 * shows a Deprecated indicator (source isn't required there). */ export function ConfigurationStatusBadges({ status }: Readonly<{
    status: ConfigurationStatus;
}>) {
    const { t } = useTranslation();
    return (<div className={" sd171 _tc171 Ub171 qb171 eb171"}>
      <TypeBadge variant={status.source === 'system_ootb' ? 'ootb' : 'custom'} label={status.source === 'system_ootb' ? t('siteContent.utilityPicker.badgeOotb') : t('siteContent.utilityPicker.badgeCustom')}/>
      {status.is_deprecated && <TypeBadge variant="deprecated" label={t('siteContent.utilityPicker.badgeDeprecated')}/>}
      {status.is_default && <TypeBadge variant="default" label={t('siteContent.utilityPicker.badgeDefault')}/>}
    </div>);
}
/**
 * A deprecated configuration is flagged by the AlertTriangle icon (slot
 * "icon") in the ComboBoxItem itself, not spelled out in the description --
 * this just appends the sunset date, which the icon alone can't convey.
 */ function optionDescription(option: CCDomainUtilityOption, providerLabel: string, t: TFunction): string {
    const base = `${option.model_name} · ${providerLabel}`;
    if (!option.is_deprecated || !option.sunset_date) return base;
    return `${base} · ${t('siteContent.utilityPicker.sunsetDate', {
        date: formatDate(option.sunset_date)
    })}`;
}
export interface UtilityConfigurationPickerProps {
    utility: string;
    label: string;
    required: boolean;
    orgId: string;
    value: string | null;
    onChange: (configurationId: string) => void;
    /** Clears this utility's selection back to "none" -- only ever offered
   * when `required` is false; a required utility must always resolve to a
   * configuration, so it has nothing to fall back to once cleared. */ onClear: () => void;
}
/**
 * One utility's configuration picker -- a react-spectrum ComboBox whose
 * items load a page at a time from API 1 (fetchDomainUtilityConfigurations),
 * using ComboBox's built-in async-loading support (`loadingState`/
 * `onLoadMore`) rather than a hand-rolled scroll/pagination UI, plus a
 * debounced search-as-you-type (the API's `search` param) instead of
 * client-side filtering, since the full option list is never fetched at
 * once. The initial selection (org default) is resolved by the caller (from
 * the org's utility-assignments) and passed in as `value`, not derived here
 * -- deriving it from whichever page happened to load first would make the
 * result depend on where the default configuration falls in the list.
 */ export const UtilityConfigurationPicker: React.FC<UtilityConfigurationPickerProps> = ({ utility, label, required, orgId, value, onChange, onClear })=>{
    const { t } = useTranslation();
    const [items, setItems] = useState<CCDomainUtilityOption[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    // The unfiltered (no search term) total from this utility's very first
    // load -- used to tell "this org has zero configurations for this utility"
    // (a real, always-blocking state) apart from "this search matched
    // nothing" (which should just show an empty list, not the blocking banner).
    const [baselineTotal, setBaselineTotal] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingState, setLoadingState] = useState<LoadingState>('loading');
    const [error, setError] = useState<string | null>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Synchronous guard: onLoadMore can fire repeatedly before `loadingState` commits.
    const loadMoreInFlightRef = useRef(false);
    // ComboBox echoes a selection back through onInputChange (it sets the input's
    // text to the chosen item's label) -- without this flag that echo would be
    // mistaken for the user typing a new search and fire a redundant reload.
    const skipNextInputChangeRef = useRef(false);
    // provider_name off the API is a raw registry key (e.g. "azure_openai");
    // resolve it to its display label the same way orgModelsColumns.tsx does.
    const { providers } = useProviderMetadata();
    const providerLabel = useCallback((providerKey: string)=>providers.find((p)=>p.key === providerKey)?.label ?? providerKey, [
        providers
    ]);
    const loadPage = useCallback(async (pageToLoad: number, searchFor: string)=>{
        setLoadingState(pageToLoad === 1 ? 'loading' : 'loadingMore');
        setError(null);
        try {
            const data = await fetchDomainUtilityConfigurations(orgId, utility, pageToLoad, PAGE_SIZE, searchFor || undefined);
            setItems((prev)=>(pageToLoad === 1 ? data.items : appendUniqueBy(prev, data.items, (o)=>o.configuration_id)));
            setTotal(data.total);
            setPage(data.page);
            if (pageToLoad === 1 && !searchFor) setBaselineTotal(data.total);
        } catch (err) {
            setError(getErrorMessage(err, t('siteContent.utilityPicker.loadError')));
        } finally{
            if (pageToLoad > 1) loadMoreInFlightRef.current = false;
            setLoadingState('idle');
        }
    }, [
        orgId,
        utility,
        t
    ]);
    useEffect(()=>{
        setItems([]);
        setTotal(0);
        setPage(1);
        setBaselineTotal(null);
        setSearchTerm('');
        loadPage(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        orgId,
        utility
    ]);
    useEffect(()=>()=>{
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        }, []);
    const handleLoadMore = useCallback(()=>{
        if (loadingState !== 'idle' || items.length >= total || loadMoreInFlightRef.current) return;
        loadMoreInFlightRef.current = true;
        loadPage(page + 1, searchTerm);
    }, [
        loadingState,
        items.length,
        total,
        page,
        searchTerm,
        loadPage
    ]);
    const handleInputChange = useCallback((newValue: string)=>{
        if (skipNextInputChangeRef.current) {
            skipNextInputChangeRef.current = false;
            return;
        }
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(()=>{
            setSearchTerm(newValue);
            loadPage(1, newValue);
        }, SEARCH_DEBOUNCE_MS);
    }, [
        loadPage
    ]);
    const hasNoConfigurationsAtAll = baselineTotal === 0;
    return (<div>
      {error ? (<div style={{
        padding: '10px 14px',
        borderRadius: 'var(--radius-300)',
        fontSize: 13,
        backgroundColor: 'var(--color-bg-validation-error, #ffebee)',
        color: 'var(--color-validation-error-text, #c62828)'
    }}>
          {error}
        </div>) : hasNoConfigurationsAtAll && required ? (<div style={{
        padding: '10px 14px',
        borderRadius: 'var(--radius-300)',
        fontSize: 13,
        backgroundColor: 'var(--color-bg-validation-error, #ffebee)',
        color: 'var(--color-validation-error-text, #c62828)'
    }}>
          {t('siteContent.utilityPicker.noModelsAllowlisted', {
        label
    })}
        </div>) : hasNoConfigurationsAtAll ? (<span className="cc-empty-config-text">{t('siteContent.utilityPicker.noAllowlistedConfigs', {
        label
    })}</span>) : (<div className={" sd171 _tc171 eb171 Ue171 qe171 Za171"}>
          <ComboBox aria-label={t('siteContent.utilityPicker.selectConfigAria', {
        label
    })} items={items} selectedKey={value} onSelectionChange={(key)=>{
        if (key != null) {
            skipNextInputChangeRef.current = true;
            onChange(String(key));
        }
    }} onInputChange={handleInputChange} loadingState={loadingState} onLoadMore={handleLoadMore} styles={" Za171"}>
            {(option: CCDomainUtilityOption)=>(<ComboBoxItem id={option.configuration_id} key={option.configuration_id} textValue={option.configuration_name}>
                {option.is_deprecated && <Alert color="notice" aria-label={t('siteContent.utilityPicker.badgeDeprecated')}/>}
                <Text slot="label">{option.configuration_name}</Text>
                <Text slot="description">{optionDescription(option, providerLabel(option.provider_name), t)}</Text>
              </ComboBoxItem>)}
          </ComboBox>
          {!required && value != null && (<ActionButton isQuiet aria-label={t('siteContent.utilityPicker.clearSelectionAria', {
        label
    })} onPress={onClear}>
              <Close/>
            </ActionButton>)}
        </div>)}
    </div>);
};
export default UtilityConfigurationPicker;

import "macro-58ad108be35a3c2c29a09c1533af46675de5f923874c75abdd1602a3ffa05693.css";
import "macro-e36bbd5f06176fedf9e7928a7d226fa7d717301c4055b4e5b77c38504e50752a.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";